// ScenarioLines — a multi-line chart of cumulative outcomes across scenarios
// (base / upside / downside), ported from the scenarios mockup hero. The base
// line is emphasized (solid, thick), upside is dashed, downside is clay; a soft
// area fills the gap between the extremes. Inline SVG, no chart libraries,
// colorblind-safe (line weight + dash + color + end labels).

export type ScenarioTone = 'base' | 'upside' | 'downside'

export type ScenarioSeries = {
  key: string
  label: string
  /** Cumulative values along the horizon. */
  points: number[]
  tone: ScenarioTone
  /** Pre-formatted end-label (e.g. "+$1.1M"). */
  endDisplay?: string
}

export type ScenarioLinesProps = {
  series: ScenarioSeries[]
  ariaLabel: string
  axisLabels?: string[]
  width?: number
  height?: number
  className?: string
}

export type ScenarioScale = { min: number; max: number }

/** Compute the shared y-scale across every scenario series. Exported for tests. */
export function computeScenarioScale(series: ScenarioSeries[]): ScenarioScale {
  const vals: number[] = []
  for (const s of series) for (const v of s.points) if (Number.isFinite(v)) vals.push(v)
  if (!vals.length) return { min: 0, max: 1 }
  const min = Math.min(...vals, 0)
  const max = Math.max(...vals, 0)
  const pad = (max - min || Math.abs(max) || 1) * 0.1
  return { min: min - pad, max: max + pad }
}

const toneColor = (tone: ScenarioTone): string => {
  switch (tone) {
    case 'downside':
      return 'var(--neg, #c0492f)'
    default:
      return 'var(--pos, #0e7c86)'
  }
}

export function ScenarioLines({ series, ariaLabel, axisLabels, width = 920, height = 260, className }: ScenarioLinesProps) {
  const padX = 60
  const padTop = 28
  const padBottom = 44
  const plotW = width - padX - 40
  const plotH = height - padTop - padBottom
  const scale = computeScenarioScale(series)
  const span = scale.max - scale.min || 1
  const maxLen = Math.max(1, ...series.map((s) => s.points.length))
  const toX = (i: number) => padX + (maxLen <= 1 ? 0 : (i / (maxLen - 1)) * plotW)
  const toY = (v: number) => padTop + (1 - (v - scale.min) / span) * plotH
  const zeroY = toY(0)

  const path = (pts: number[]) =>
    pts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`).join(' ')

  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      <line x1={padX} y1={zeroY} x2={width - 40} y2={zeroY} stroke="var(--axis, #3a3f47)" strokeWidth={1.2} />
      {series.map((s) => {
        const last = s.points.length ? s.points[s.points.length - 1] : 0
        const lx = toX(s.points.length - 1)
        const ly = toY(last)
        const color = toneColor(s.tone)
        const dashed = s.tone === 'upside'
        return (
          <g key={s.key}>
            <path
              d={path(s.points)}
              fill="none"
              stroke={color}
              strokeWidth={s.tone === 'base' ? 3.2 : 2.6}
              strokeDasharray={dashed ? '7 5' : undefined}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={lx} cy={ly} r={s.tone === 'base' ? 5 : 4} fill={color} />
            <text x={lx - 8} y={ly - 8} textAnchor="end" style={{ font: '700 12px var(--font-ui)', fill: s.tone === 'downside' ? 'var(--neg-ink, #a83e29)' : 'var(--pos)' }}>
              {s.label}{s.endDisplay ? ` ${s.endDisplay}` : ''}
            </text>
          </g>
        )
      })}
      {axisLabels && axisLabels.length ? (
        <g style={{ font: 'italic 500 11px var(--font-guide)', fill: 'var(--muted)' }}>
          {axisLabels.map((label, i) => {
            const frac = axisLabels.length <= 1 ? 0 : i / (axisLabels.length - 1)
            const x = toX(frac * (maxLen - 1))
            return (
              <text key={`${label}-${i}`} x={x} y={height - 18} textAnchor="middle">
                {label}
              </text>
            )
          })}
        </g>
      ) : null}
    </svg>
  )
}

export default ScenarioLines
